import type { ReactNode } from 'react';

import { useDreamComponentsContext } from '../dreamComponentProvider/hooks';
import { StyledTextLeaf, TextLeaf } from './Text';
import type { Mark } from './Text.types';

interface Props {
  nodeId?: string;
  /** The block's leaves, as serialized. Rendered whenever nothing is editing this block. */
  children: ReactNode;
  /** The leading leaf's marks — the theme's textStyle is taken from here. */
  marks?: Mark[];
}

const EMPTY_LEAF = { text: '' };

/**
 * A text block's copy, or a host's editable surface in its place.
 *
 * The surface is wrapped in the same TextLeaf/StyledTextLeaf pair the copy would have had, which
 * is what carries the block's typography: font family, weight and colour come from the styled
 * span's inline style, and font size and line height from a stylesheet rule scoped to the
 * TextLeaf class (see Text.css.ts). An editor rendering its own spans would lose both.
 */
export function EditableTextSlot({ nodeId, children, marks }: Props) {
  const { renderEditableText } = useDreamComponentsContext();

  const editable = nodeId && renderEditableText ? renderEditableText(nodeId) : null;
  if (!editable) return <>{children}</>;

  const textStyle = marks?.find((mark) => mark.type === 'textStyle');

  return (
    <TextLeaf leaf={EMPTY_LEAF}>
      {textStyle ? (
        <StyledTextLeaf leaf={EMPTY_LEAF} mark={textStyle}>
          {editable}
        </StyledTextLeaf>
      ) : (
        editable
      )}
    </TextLeaf>
  );
}
